import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { afterServiceStateType, afterServiceType, formattedDateTime, getCurrentDateTime } from 'pages/utils/commonUtils';
import { PrimaryButton, WhiteButton } from "components/common/Button";
import { AfterService, AfterServiceAnswer, Recorder } from "type/type";
import { upsertAfterServiceAnswer, decryptAES256, getAfterServiceOne, createAfterService, getUserById, getRecorderList, maxLengthCheck } from "api/api";
import Loading from "components/common/Loading";
import { AdminLoginInfoContext } from "pages/Layout";
import { useForm } from "react-hook-form";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:last-of-type {
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .process-content {
    > div {
      max-width: 700px;
    }
    input {
      min-width: 150px;
      max-width: 400px;
      // width: 50%;
    }

    .select-wrap {
      position: relative;
      min-width: 150px;
      max-width: 400px;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        right: 20px;
        border: 5px solid transparent;
        border-top-color: #313a43;
        transform: scaleY(1.4);
      }
      &::before {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 95%;
        top: 0;
        right: 48px;
        background-color: #e7e7e7;
        z-index: 0;
      }
    }
  }
  
  .process-content2 {
    > div {
      max-width: 1400px;
    }
  }

  .err-border {
    border: 1px solid red !important;
  }
`;

const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem;
  gap: 1rem;
  // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
  // border-bottom: 1px solid #666;

  label {
    width: 6rem;
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  textarea {
    resize: none;
    min-height: 10rem;
  }
  input:not([type="radio"]), textarea {
    width: calc(100% - 6rem);
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    &:focus-visible {
      outline: none;
      &:not(:disabled, :read-only) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:read-only {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }
  
  select {
    border: none;
    border-radius: 0.25rem;
    background-color: #f1f1f1;
    
    &:focus-visible {
      outline: none;
      &:not(:disabled) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:disabled {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }

  input[type="radio"] {
    appearance: none;
    width: 1rem;
    height: 1rem;
    border-radius: 0.25rem;
    background-color: #fff;
    &:checked {
      background-color: ${prop => prop.theme.primaryColor};
    }
  }
  &.state-wrap {
    > div {
      align-items: center;
      gap: 0.5rem;
      label {
        width: auto;
      }
    }
  }
  &.as-content {
    span {
      width: calc(100% - 6rem);
      padding: 0.5rem;
      border-bottom: 1px solid #666;
      cursor: default;
      font-size: 14px;
      line-height: 20px;
      word-break: break-all;
      overflow-wrap: break-word;
    }
  }
`;

// A/S 접수 처리
function AfterServiceProcess() {
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  // 목록에서 전달 받은 값
  const { asIdx } = useParams();
  const [loading, setLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
  const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
  
  // useForm
  const { register, formState: { errors, isValid }, trigger, setValue } = useForm<any>({
    mode: 'all',
  });
  
  const [recorderList, setRecorderList] = useState<Recorder[]|null>(null);

  const currentDateTime = getCurrentDateTime();
  const [asData, setAsData] = useState<AfterService>({
    idx: Number(asIdx),
    user_id: '',
    user_name: '',
    user_mobile: '',
    user_email: '',
    title: '',
    content: '',
    reg_name: '',
    model: '',
    state: 0,
    as_type: 'APP',
    answer_type: 'Mobile',
    create_date: currentDateTime,
    update_date: currentDateTime,
  });
  const [asAnswerData, setAsAnswerData] = useState<AfterServiceAnswer>({
    idx: 0,
    after_service_idx: Number(asIdx),
    content: '',
    write_id: adminLoginInfo.id,
    create_date: currentDateTime,
    update_date: currentDateTime,
    state: 0,
  });
  
  // 문의 내용 변경
  const handleAsDataChange = (event: any) => {
    const { name, value } = event.target;
    setAsData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // 설치위치 변경
  const handleRegNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRegName = event.target.value;

    const selectedRecorder = recorderList?.find((recorder) => recorder.reg_name === selectedRegName);

    if (selectedRecorder) {
      setAsData((prev) => ({
        ...prev,
        reg_name: selectedRecorder.reg_name,
        model: selectedRecorder.model || '',
      }));
    }
  };

  // 답변 내용 변경
  const handleChange = (event: any) => {
    const { value } = event.target;
    setAsAnswerData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  // 진행 상태 변경
  const handleStateChange = (event: any) => {
    const { name, value } = event.target;
    setAsAnswerData((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  // A/S 답변 등록
  const submitAs = async () => {
    if (!asAnswerData.content) {
      alert('답변 내용을 작성해주세요');
      return;
    }
    const response = await upsertAfterServiceAnswer(asAnswerData);
    if (response.code === 200) {
      alert(`답변이 저장되었습니다.`);
      navigate(`/admin/after-service`);
    } else {
      alert(response.message);
    }
  };

  // 사용자 ID 조회 함수
  const getUserId = async () => {
    try {
      if (!asData.user_id) {
        alert('고객 ID를 입력해주세요.');
        return;
      }

      const response = await getUserById(asData.user_id);
      if (response.code !== 200 || !response.data) {
        alert('존재하지 않는 ID 입니다.');
        return;
      }

      // 사용자 정보 해독
      const { name, email, mobile } = response.data;
      const user_mobile = decryptAES256(mobile, REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV);

      // Recorder 데이터 조회
      const recorderRes = await getRecorderList(0, 0, { user_id: asData.user_id });
      const firstRecorder = recorderRes?.data?.[0];

      // 상태 업데이트 (한 번에 처리)
      setAsData((prev) => ({
        ...prev,
        user_name: name,
        user_email: email,
        user_mobile,
        reg_name: firstRecorder?.reg_name ?? prev.reg_name,
        model: firstRecorder?.model ?? prev.model,
      }));

      // Form 값 설정 (한 번에 처리)
      const formValues = {
        user_name: name,
        user_email: email,
        user_mobile,
        reg_name: firstRecorder?.reg_name,
        model: firstRecorder?.model,
      };

      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== undefined) setValue(key, value, { shouldValidate: true });
      });

      // Recorder 목록 설정
      if (recorderRes.code === 200) {
        setRecorderList(recorderRes.data);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      alert("사용자 데이터를 가져오는 도중 오류가 발생했습니다.");
    }
  };

  // A/S 수기 등록
  const submitAsManual = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    if (asAnswerData.content?.length) {
      asData.answer = asAnswerData;
    }

    const response = await createAfterService(asData);
  
    if (response.code === 200) {
      alert("AS가 접수되었습니다.");
      navigate("/admin/after-service");
    } else {
      alert(response.message);
    }
  };

  // AS 리스트 가져오기
  const getAfterService = useCallback(async () => {
    try {
      if (Number(asIdx) === 0) {
        setIsReadOnly(true);
        setLoading(true);
        return;
      }
  
      setLoading(true); // 🔥 요청 시작 전에 로딩 설정
      const response = await getAfterServiceOne(Number(asIdx));
  
      if (response.code !== 200) {
        alert("유효하지 않은 값입니다.");
        navigate(`/admin/after-service`);
        return;
      }
  
      const { data } = response;
  
      // user_mobile 복호화 (데이터가 있는 경우만 처리)
      if (data.user_mobile) {
        data.user_mobile = decryptAES256(data.user_mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
      }
  
      // AS 데이터 상태 저장
      setAsData(data);
  
      // AS 답변 데이터 상태 저장
      setAsAnswerData(prev => ({
        ...prev,
        ...(data.answer ?? {}), // ✅ undefined 방지
        idx: data.answer?.idx ? Number(data.answer.idx) : prev.idx, 
        state: Number(data.state),
        after_service_idx: Number(data.idx),
      }));
  
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false); // 🔥 요청이 끝나면 무조건 로딩 종료
    }
  }, [asIdx, navigate]);

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getAfterService();
  }, [getAfterService]);
  return (
    <>
    {loading ? <Loading /> :
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span>A/S접수 처리</span>
            </p>
          </div>
          <div>
            {isReadOnly ?
              <PrimaryButton type="button" onClick={submitAsManual}>저장</PrimaryButton>
            :
              <PrimaryButton type="button" onClick={submitAs} disabled={!asAnswerData.content}>저장</PrimaryButton>
            }
            <WhiteButton type="button" onClick={() => navigate('/admin/after-service')}>목록</WhiteButton>
          </div>
        </TitleAndBtnContainer>
        <FlexCol>
          <FlexRow className="process-content">
            <InputWrap>
              <label>제목</label>
              <input type='text' value={asData.title} readOnly={!isReadOnly} className={`${errors.title && 'err-border'}`} placeholder="제목을 입력해주세요." maxLength={50}
                {...register("title", {
                  required: {
                    value: true,
                    message: '제목을 입력해주세요.'
                  },
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleAsDataChange(e);
                  }
                })}
              />
            </InputWrap>
            <InputWrap>
              <label>등록일자</label>
              <input type='text' value={ formattedDateTime(asData.create_date) } readOnly />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>고객ID</label>
              <input type='text' value={ asData.user_id } readOnly={!isReadOnly} className={`${errors.user_id && 'err-border'}`} placeholder="고객 ID를 입력해주세요." maxLength={45}
                {...register("user_id", {
                  required: true,
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleAsDataChange(e);
                  }
                })}
              />
              {isReadOnly && <PrimaryButton type="button" onClick={getUserId}>조회</PrimaryButton>}
            </InputWrap>
            <InputWrap>
              <label>고객명</label>
              <input type='text' value={ asData.user_name} readOnly={!isReadOnly} className={`${errors.user_name && 'err-border'}`} placeholder="고객명을 입력해주세요." maxLength={45}
                {...register("user_name", {
                  required: true,
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleAsDataChange(e);
                  }
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>문의 유형</label>
              {!isReadOnly ?
                <input type='text' value={ asData.as_type } readOnly />
              :
                <div className="select-wrap">
                  <select value={asData.as_type}
                    {...register("as_type", {
                      required: true,
                      onChange: handleAsDataChange
                    })}
                  >
                    {afterServiceType.map((type: string, index: number) => 
                      <option key={`as_type_${index}`} value={type}>{type}</option>
                    )}
                  </select>
                </div>
              }
            </InputWrap>
            <InputWrap>
              <label>답변 유형</label>
              {!isReadOnly ?
                <input type='text' value={ asData.answer_type } readOnly />
              :
                <div className="select-wrap">
                  <select value={asData.answer_type} 
                    {...register("answer_type", {
                      required: true,
                      onChange: handleAsDataChange
                    })}
                  >
                    <option value={'Mobile'}>Mobile</option>
                    {/* <option value={'Email'}>E-Mail</option> */}
                  </select>
                </div>
              }
              {/* 답변 유형에 따라서 추가 문구 설정 필요 */}
              {/* <input type='text' value={`${asData.sendType} - 연락받을 연락처: ${asData.tempPhone}`} readOnly={!isReadOnly} /> */}
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            {asData.answer_type === "Mobile" ? 
              <InputWrap>
                <label>연락처</label>
                <input type='text' value={ asData.user_mobile } readOnly={!isReadOnly} className={`${errors.user_mobile && 'err-border'}`} placeholder="연락처를 입력해주세요." maxLength={11}
                  {...register("user_mobile", {
                    required: true,
                    pattern : /^01([0|1|6|7|8|9])([0-9]{3,4})([0-9]{4})$/g,
                    onChange: (e) => {
                      maxLengthCheck(e);
                      handleAsDataChange(e);
                    }
                  })}
                />
              </InputWrap>
            :
              <InputWrap>
                <label>이메일</label>
                <input type='text' value={ asData.user_email } readOnly={!isReadOnly} className={`${errors.user_email && 'err-border'}`} placeholder="이메일을 입력해주세요." maxLength={45}
                  {...register("user_email", {
                    required: true,
                    pattern: /^[a-zA-Z0-9+-\_.]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/g,
                    onChange: (e) => {
                      maxLengthCheck(e);
                      handleAsDataChange(e);
                    }
                  })}
                />
              </InputWrap>
            }
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>설치위치</label>
              {!recorderList || recorderList.length === 0 ? (
                <input
                  type="text"
                  value={asData.reg_name}
                  readOnly={!isReadOnly}
                  className={`${errors.reg_name ? 'err-border' : ''}`}
                  placeholder="설치 위치를 입력해주세요."
                  maxLength={50}
                  {...register("reg_name", {
                    required: true,
                    onChange: (e) => {
                      maxLengthCheck(e);
                      handleAsDataChange(e);
                    }
                  })}
                />
              ) : (
                <div className="select-wrap">
                  <select
                    disabled={!isReadOnly}
                    {...register("reg_name", {
                      required: true,
                      onChange: handleRegNameChange,
                    })}
                  >
                    <option value="" disabled>
                      설치 위치를 선택해주세요
                    </option>
                    {recorderList.map((recorder, index) => (
                      <option key={index} value={recorder.reg_name}>
                        {recorder.reg_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </InputWrap>
            <InputWrap>
              <label>모델</label>
              <input type='text' value={ asData.model } readOnly={!isReadOnly} className={`${errors.model && 'err-border'}`} placeholder="모델을 입력해주세요." maxLength={20}
                {...register("model", {
                  required: true,
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleAsDataChange(e);
                  }
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content2">
            <InputWrap className="as-content">
              <label>
                <p>문의 내용</p>
                <p>{asData.content.length}/500 자</p>
              </label>
              <textarea id="content" placeholder="문의 내용을 입력하세요." value={asData.content} className={`${errors.content && 'err-border'}`} readOnly={!isReadOnly} maxLength={500}
                {...register("content", {
                  required: true,
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleAsDataChange(e);
                  }
                })}
              ></textarea>
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content2">
            <InputWrap>
              <label htmlFor="answer-content">
                <p>답변</p>
                <p>{asAnswerData?.content?.length || 0}/500 자</p>
              </label>
              <textarea id="answer-content" placeholder="답변 내용을 입력하세요." value={asAnswerData.content} className={`${errors.answer_content && 'err-border'}`} maxLength={500}
                {...register("answer_content", {
                  required: !isReadOnly,
                  onChange: (e) => {
                    maxLengthCheck(e);
                    handleChange(e);
                  }
                })}
              ></textarea>
            </InputWrap>
          </FlexRow>
          <InputWrap className="state-wrap">
            <label>진행 상태</label>
            <FlexRow>
              {afterServiceStateType.map((state: string, index: number) => 
                <Fragment key={index}>
                  <input
                    type="radio"
                    id={`state_${index}`}
                    value={index}
                    checked={asAnswerData.state === index}
                    {...register("state", {
                      required: true,
                      onChange: handleStateChange
                    })}
                  />
                  <label htmlFor={`state_${index}`}>{state}</label>
                </Fragment>
              )}
            </FlexRow>
          </InputWrap>
        </FlexCol>
      </Container>
    }
    </>
  );
}

export default AfterServiceProcess;